import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFoundPage = () => {
    return (
        <section className="min-h-screen bg-white dark:bg-[#080a0f] pt-28 pb-10 flex items-center justify-center overflow-hidden font-sans">

            {/* Immersive 90% Dashboard Container */}
            <div className="w-[90%] h-[85vh] mx-auto relative flex flex-col lg:grid lg:grid-cols-12 gap-6">

                {/* --- MAIN PANEL: The Error Scanner (8 Cols) --- */}
                <div className="lg:col-span-8 bg-gray-50 dark:bg-surface-dark/40 rounded-[2.5rem] border border-gray-100 dark:border-white/5 relative overflow-hidden flex flex-col justify-center p-8 md:p-20 group">

                    {/* 1. The "Scan Line" Animation (Tech Vibe) */}
                    <div className="absolute inset-0 pointer-events-none opacity-20 dark:opacity-40">
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-primary/50 shadow-[0_0_15px_rgba(0,180,216,0.8)] animate-[scan_4s_linear_infinite]"></div>
                    </div>

                    {/* 2. Top Metadata */}
                    <div className="reveal flex items-center gap-4 mb-10">
                        <div className="px-3 py-1 rounded-md bg-primary text-white text-[10px] font-black uppercase tracking-widest">
                            System Alert
                        </div>
                        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest animate-pulse">
                            Packet Loss Detected...
                        </span>
                    </div>

                    {/* 3. The Headline (Matches your Landing Page Font) */}
                    <h1 className="text-6xl md:text-[8rem] font-bold text-[#0d121b] dark:text-white font-display leading-[0.9] tracking-tighter mb-8">
                        Broken <br />
                        <span className="text-primary italic">Connection.</span>
                    </h1>

                    <p className="text-gray-500 dark:text-gray-400 text-lg md:text-xl max-w-lg leading-relaxed font-medium mb-12">
                        The requested route has been dereferenced or is currently offline. Your current session has reached a <span className="text-primary font-mono">Null Pointer</span>.
                    </p>

                    {/* 4. Action Buttons (Matches Landing Page CTAs) */}
                    <div className="flex flex-wrap gap-4">
                        <Link
                            to="/"
                            className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all transform hover:scale-105 shadow-lg shadow-primary/20 flex items-center gap-3 group"
                        >
                            Reconnect to Base
                            <span className="material-symbols-outlined text-sm group-hover:rotate-12 transition-transform">lan</span>
                        </Link>
                        <a
                            href="/#contact"
                            className="px-8 py-4 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-white font-bold text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                        >
                            Log Incident
                        </a>
                    </div>

                    {/* Massive 404 Depth Background */}
                    <div className="absolute -bottom-20 -right-10 text-[25rem] font-black text-primary/5 select-none pointer-events-none font-display">
                        404
                    </div>
                </div>

                {/* --- SIDE PANEL: Technical Stats (4 Cols) --- */}
                <div className="lg:col-span-4 flex flex-col gap-6">

                    {/* "Live" Terminal Stats Card */}
                    <div className="flex-grow bg-[#0d121b] rounded-[2.5rem] p-8 text-white relative overflow-hidden border border-white/5">
                        <div className="flex justify-between items-center mb-8">
                            <h3 className="text-xs font-black uppercase tracking-[0.3em] text-primary">Terminal v2.0</h3>
                            <div className="flex gap-1.5">
                                <div className="size-2 rounded-full bg-red-500/50"></div>
                                <div className="size-2 rounded-full bg-yellow-500/50"></div>
                                <div className="size-2 rounded-full bg-green-500/50"></div>
                            </div>
                        </div>

                        <div className="space-y-4 font-mono text-[11px] leading-loose">
                            <p className="text-green-400">/ portfolio_root $ <span className="text-white">fetch --route</span></p>
                            <p className="text-gray-500 italic">... Searching directories</p>
                            <p className="text-red-400 font-bold">[!] ERROR: REQUEST_FAILED</p>
                            <p className="text-gray-400 underline decoration-primary">Reason: Path_Unreachable</p>
                            <div className="pt-4 mt-4 border-t border-white/5">
                                <p className="text-gray-500 uppercase mb-2">Network Diagnostics:</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-primary">LATENCY</p>
                                        <p className="text-white text-lg font-bold">0ms</p>
                                    </div>
                                    <div>
                                        <p className="text-primary">UPTIME</p>
                                        <p className="text-white text-lg font-bold">99.9%</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tech Abstract Background */}
                        <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-primary/10 to-transparent pointer-events-none"></div>
                    </div>

                    {/* Smaller Quick Action Card */}
                    <div className="bg-primary rounded-[2.5rem] p-8 flex items-center justify-between group cursor-pointer hover:shadow-glow hover:shadow-primary/30 transition-all">
                        <div className="text-white">
                            <h4 className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">System Navigation</h4>
                            <p className="text-lg font-bold font-display">View Projects</p>
                        </div>
                        <Link to="/#projects" className="size-12 rounded-xl bg-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-primary transition-all">
                            <span className="material-symbols-outlined">rocket_launch</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Global Animation Styles */}
            <style>{`
                @keyframes scan {
                    0% { top: 0%; opacity: 0; }
                    5% { opacity: 1; }
                    95% { opacity: 1; }
                    100% { top: 100%; opacity: 0; }
                }
            `}</style>
        </section>
    );
};

export default NotFoundPage;